import { Obstacle } from "../Obstacle";
import { eObstacleType } from "../ObstacleTypes";
import { Game, eModelAsset, eAtlas_0, eSfxSprite, SfxSprites, eAudioAsset } from "../../../../Game";
import { Gameplay, eBatchGroup } from "../../../Gameplay";
import { Player } from "../../Player/Player";
import { RotatorObsVer } from "./RotatorObsVer";
import { RotatorObsMapData } from "./RotatorObsMapData";
import { ViewCellMapRow } from "../../View/ViewCellMapRow";
import { TextureAtlasUtils } from "../../../../Utils/TextuAtlasUtils";
import { SoundManager } from "../../../../Utils/SoundManager";



export class RotatorObs extends Obstacle {
    private _mapData: RotatorObsMapData;

    /**
     * Entita pro pohybujici se cast kladiva
     */
    private _entity2: pc.Entity;

    /**
     * Offset stinu pohybujici se casti kladiva od pozice prekazky
     */
    private static readonly _SHADOW_OFFSET = 1;

    /**
     * Entita se stinem pohybujici se casti kladiva
     */
    private _shadow2: pc.Entity;

    /**
     * Lokalni pozice rotujici casti kladiva na Y
     */
    private static readonly _HAMMER_Y = 0.48;

    /**
     * Polomer hlavy kladiva
     */
    private static readonly _HEAD_RADIUS = 0.35;

    /**
     * Offset hlavy kladiva od pozice prekazky na ose X
     */
    private static readonly _HEAD_OFFSET = 1;

    /**
     * Aktualni rotace kladiva ve stupnich (0 az 360)
     */
    private _headRotation: number;

    private _playSfx: boolean;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        super(eObstacleType.rotator);

        this._entity2 = new pc.Entity();
        this._entity2.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.hammer, pc.eAssetType.model),
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.solid_0],
        });

        SoundManager.createSoundComponent(this._entity2, {
            positional: true,
            distanceModel: pc.DISTANCE_LINEAR,
            maxDistance: 10
        });

        SoundManager.addSoundSlot(this._entity2, eSfxSprite.hammerSpin, SfxSprites[eSfxSprite.hammerSpin], Game.sfxSpriteAssetId);

        this._entity2.setLocalPosition(0, RotatorObs._HAMMER_Y, 0);
        this._entity.addChild(this._entity2);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(mapData: RotatorObsMapData, row: ViewCellMapRow): void {
        super.init(mapData, row);

        this._mapData = mapData;
        this._playSfx = false;

        // Stin
        let atlas = Game.getTextureAtlas(0);

        this._shadow2 = Obstacle._SHADOW_POOL.getItem();
        this._shadow2.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[eAtlas_0.shadowSquare]).rect);
        this._shadow2.element.width = 1;
        this._shadow2.element.height = 1;
        this._shadow2.setLocalPosition(0, RotatorObs._SHADOW_OFFSET, RotatorObs._HAMMER_Y - 0.01);
        this._shadow2.setLocalEulerAngles(180, 0, 0);
        this._shadow2.enabled = true;

        if (this._shadow2.parent != this._entity2)
            this._shadow2.reparent(this._entity2);

        // Nastavit rotaci
        this.updateRotation();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    release(): void {
        super.release();

        this._shadow2.enabled = false;
        this._shadow2.setLocalEulerAngles(-90, 0, 0);
        Obstacle._SHADOW_POOL.returnItem(this._shadow2);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (!super.update())
            return false;

        this.updateRotation();

        return true;
    }

    //-------------------------------------------------------------------------
    /**
     * Nastavit rotaci kladiva
     */
    //-------------------------------------------------------------------------
    private updateRotation(): void {
        // Vypocitat za kolik vterin hrac dosahne pozice stredu prekazky na ose Z
        let pl = Player.instance;
        let pathTime = (pl.position.z - this._row.z) / pl.rollingSpeed;
        let behindPl = false;

        if (pathTime < 0) {
            pathTime *= -1;
            behindPl = true;
        }

        // Parametry rotace kladiva
        let ver = this._version as RotatorObsVer;

        // Hodnoty pro otoceni o 90 stupnu
        let rot90Delay = ver.stopLen;
        let rot90Speed = ver.rotationSpeed;
        let rot90Time = rot90Speed + rot90Delay;

        // Doba otoceni o 360 stupnu
        let rot360Time = rot90Time * 4;

        // Pozice v otoceni o 360 stupnu ve vterinach
        let rot360Pos = pathTime % rot360Time;

        // Pozice v otoceni o 90 stupnu ve vterinach
        let rot90Pos = rot360Pos % rot90Time;

        // Prepocitat pozici v otoceni o 90 stupnu na rozsah 0 az 1
        if (rot90Pos < rot90Delay / 2) {
            rot90Pos = 0;
            this._playSfx = true;
        }
        else {
            if (this._playSfx) {
                this._playSfx = false;
                SoundManager.playSound(this._entity2, eSfxSprite.hammerSpin);
            }

            rot90Pos = (rot90Pos - rot90Delay / 2) / rot90Speed;

            if (rot90Pos > 1)
                rot90Pos = 1;
        }

        // Vypocitat uhel rotace v rosahu 0 az 360 stupnu        
        let rotDir = this._mapData.rotationDir;
        let rotAngle = Math.floor(rot360Pos / rot90Time) * 90;

        if (behindPl) {
            rotDir *= -1;
            rotAngle += ver.rotationEase(rot90Pos) * 90;
        }
        else {
            rotAngle += (90 - ver.rotationEase(1 - rot90Pos) * 90);
        }

        this._entity2.setLocalEulerAngles(90, (this._headRotation = this._mapData.targetRotation - (rotAngle * rotDir)) + 90, 0);
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci 3D colizni oblast prekazky (bud pc.BoundingBox nebo pc.BoundingSphere)
     * @param pos Aktualni pozice prekazky v hernim svete
     * @param id Index kolizni oblasti
     */
    //-------------------------------------------------------------------------
    protected getColArea(pos: pc.Vec3, id: number): pc.BoundingBox | pc.BoundingSphere {
        if (id == 0)
            // Sloupek
            return super.getColArea(pos, id);

        // Hlava kladiva
        let headAngle = this._headRotation * Math.PI / 180;
        let colArea = Obstacle._TMP_BSPHERE;

        colArea.radius = RotatorObs._HEAD_RADIUS;
        colArea.center.set(
            pos.x + Math.cos(headAngle) * RotatorObs._HEAD_OFFSET,
            pos.y,
            pos.z - Math.sin(headAngle) * RotatorObs._HEAD_OFFSET,
        );

        return colArea;
    }
}